import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionSummary, AccordionDetails, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReleaseNotesService, ReleaseNote } from "../../services/release-notes.service";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../models";
import { Button } from "../../components";

const ReleaseNotesList = () => {
  const toast = useContext(ToastContext);
  const navigate = useNavigate();
  const [releases, setReleases] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedMainVersions, setExpandedMainVersions] = useState<Set<string>>(new Set());
  const [expandedReleases, setExpandedReleases] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchReleases();
  }, []);

  const fetchReleases = async () => {
    setLoading(true);
    try {
      const data = await ReleaseNotesService.getReleases();
      setReleases(data);
    } catch (error) {
      toast?.openToast("Failed to load release notes", 2000, ToastVariants.Error);
    } finally {
      setLoading(false);
    }
  };

  const handleMainVersionChange = (version: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    const newExpanded = new Set(expandedMainVersions);
    if (isExpanded) {
      newExpanded.add(version);
    } else {
      newExpanded.delete(version);
    }
    setExpandedMainVersions(newExpanded);
  };

  const handleReleaseChange = (releaseId: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    const newExpanded = new Set(expandedReleases);
    if (isExpanded) {
      newExpanded.add(releaseId);
    } else {
      newExpanded.delete(releaseId);
    }
    setExpandedReleases(newExpanded);
  };

  const formatMainVersion = (version: number | string): string => {
    return `Version ${version}`;
  };

  const formatSubVersion = (release: ReleaseNote): string => {
    return `Version ${release.version}.${release.release}`;
  };

  const formatDate = (dateString: string): string => {
    try {
      // Handle different date formats
      // If it's in DD/MM/YY format, parse it
      if (dateString.includes("/")) {
        const [day, month, year] = dateString.split("/");
        const fullYear = year.length === 2 ? `20${year}` : year;
        const date = new Date(`${fullYear}-${month}-${day}`);
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      }
      // If it's in ISO format or other standard formats
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if parsing fails
      }
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch (error) {
      return dateString;
    }
  };

  const formatVersionWithDate = (release: ReleaseNote): string => {
    const version = formatSubVersion(release);
    const date = formatDate(release.releaseDate);
    return `${version} (${date})`;
  };

  // Group releases by main version
  const groupedReleases = releases.reduce((acc, release) => {
    const versionKey = String(release.version);
    if (!acc[versionKey]) {
      acc[versionKey] = [];
    }
    acc[versionKey].push(release);
    return acc;
  }, {} as Record<string, ReleaseNote[]>);

  // Sort versions in descending order (newest first)
  const sortedVersions = Object.keys(groupedReleases).sort((a, b) => {
    const numA = Number(a);
    const numB = Number(b);
    return numB - numA;
  });

  // Sort releases within each version group by release number (descending)
  sortedVersions.forEach(version => {
    groupedReleases[version].sort((a, b) => {
      const releaseA = parseFloat(a.release) || 0;
      const releaseB = parseFloat(b.release) || 0;
      return releaseB - releaseA;
    });
  });

  return (
    <main>
      <div className="container mx-auto p-8">
        <div className="block lg:flex justify-between items-center mb-6">
          <div className="mb-5 lg:mb-0">
            <h2 className="heading mb-0 uppercase whitespace-nowrap">Release Notes</h2>
          </div>
          <div className="block md:flex lg:justify-end flex-wrap w-full">
            <Button text="Create new release note" onClick={() => navigate("/version_info/update")} className="whitespace-nowrap w-auto mt-0" />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <CircularProgress />
          </div>
        ) : releases.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No release notes available</p>
          </div>
        ) : (
          <div className="space-y-0">
            {sortedVersions.map(versionKey => {
              const versionReleases = groupedReleases[versionKey];
              const mainVersionId = `version-${versionKey}`;
              const isMainExpanded = expandedMainVersions.has(versionKey);

              return (
                <div key={mainVersionId} className="border-b border-gray-200">
                  {/* Main Version Accordion */}
                  <Accordion
                    expanded={isMainExpanded}
                    onChange={handleMainVersionChange(versionKey)}
                    sx={{
                      boxShadow: "none",
                      "&:before": {
                        display: "none",
                      },
                      backgroundColor: "transparent",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#9ca3af" }} />}
                      sx={{
                        padding: "16px 0",
                        "& .MuiAccordionSummary-content": {
                          margin: "0 !important",
                        },
                      }}
                    >
                      <span className="text-lg font-medium">{formatMainVersion(versionKey)}</span>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: "0", backgroundColor: "transparent" }}>
                      {/* Sub-releases within this version */}
                      <div className="space-y-0 pl-2">
                        {versionReleases.map((release, index) => {
                          const releaseId = release.id || `release-${versionKey}-${release.release}-${index}`;
                          const isReleaseExpanded = expandedReleases.has(releaseId);

                          return (
                            <div key={releaseId} className="border-b border-gray-300 last:border-b-0">
                              <Accordion
                                expanded={isReleaseExpanded}
                                onChange={handleReleaseChange(releaseId)}
                                sx={{
                                  boxShadow: "none",
                                  padding: "0 10px",
                                  "&:before": {
                                    display: "none",
                                  },
                                  "&.Mui-expanded": {
                                    backgroundColor: "#f5f5f5",
                                  },
                                  backgroundColor: "transparent",
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon sx={{ color: "#9ca3af" }} />}
                                  sx={{
                                    padding: "12px 0",
                                    "& .MuiAccordionSummary-content": {
                                      margin: "0 !important",
                                    },
                                  }}
                                >
                                  <span className="text-base font-medium">{formatVersionWithDate(release)}</span>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: "0 0 24px 0", backgroundColor: "transparent" }}>
                                  {release.line && <h3 className="text-xl font-bold mb-4">{release.line}</h3>}
                                  <div
                                    className="release-notes-content prose max-w-none"
                                    style={{
                                      lineHeight: "1.75",
                                    }}
                                    dangerouslySetInnerHTML={{ __html: release.details }}
                                  />
                                </AccordionDetails>
                              </Accordion>
                            </div>
                          );
                        })}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

export default ReleaseNotesList;
